import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiDelete, apiGet, apiPatch, ApiError } from '../api/client';
import type { AccountResponse, HistoryDeleteResponse } from '../api/types';
import { initialsOf, useAccount } from '../auth/AccountContext';
import Icon from '../components/Icon';
import TopAppBar, { AccountActionButton } from '../components/TopAppBar';

/**
 * Settings view — Obsidian Companion layout: a sticky left category nav and
 * grouped section cards on the right. Real backend behavior is preserved:
 *   • transcript-sync toggle → PATCH /account/preferences
 *   • clear cloud history    → DELETE /history (with confirm)
 * Profile/subscription state comes from the shared AccountContext instead of
 * a private GET /account. Appearance and AI-writing defaults are display-only
 * (no backend) and rendered as static rows.
 */

interface CategoryItem {
  id: string;
  label: string;
  icon: string;
}

const CATEGORIES: CategoryItem[] = [
  { id: 'account', label: 'Account', icon: 'person' },
  { id: 'appearance', label: 'Appearance', icon: 'palette' },
  { id: 'ai-writing', label: 'AI writing', icon: 'magic_button' },
  { id: 'dictionary', label: 'Personal dictionary', icon: 'book' },
  { id: 'privacy', label: 'Privacy', icon: 'shield' },
  { id: 'about', label: 'About', icon: 'info' },
  { id: 'legal', label: 'Legal', icon: 'gavel' },
];

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

export default function SettingsView() {
  const { user, loading: accountLoading, error: accountError, refresh } = useAccount();

  const [activeSection, setActiveSection] = useState('account');

  // Transcript-sync source of truth mirrors the account context; tracked
  // locally so the toggle can update immediately on a successful PATCH.
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [cloudHasData, setCloudHasData] = useState(false);
  const [cloudLoading, setCloudLoading] = useState(true);
  const [cloudError, setCloudError] = useState<string | null>(null);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Delete-cloud-history confirmation + status.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deletedCount, setDeletedCount] = useState<number | null>(null);
  const cloudDeleteTriggerRef = useRef<HTMLButtonElement | null>(null);
  const deleteDialogRef = useRef<HTMLDivElement | null>(null);
  const deleteCancelRef = useRef<HTMLButtonElement | null>(null);
  const deleteDialogWasOpenRef = useRef(false);

  // Display-only AI toggle (no backend).
  const [autoApply, setAutoApply] = useState(true);

  // Sync the local toggle from the shared account context.
  useEffect(() => {
    if (user) setSyncEnabled(user.transcript_sync_enabled);
  }, [user]);

  // Scroll-spy: highlight the nav entry for the section currently in view.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );
    for (const cat of CATEGORIES) {
      const el = document.getElementById(cat.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  const loadCloudData = useCallback(() => {
    setCloudLoading(true);
    setCloudError(null);
    apiGet<{ entries: unknown[]; nextCursor: string | null }>('/history?limit=1')
      .then((hist) => setCloudHasData(hist.entries.length > 0))
      .catch((err: unknown) => {
        setCloudError(err instanceof ApiError ? err.message : 'Failed to load history');
      })
      .finally(() => setCloudLoading(false));
  }, []);

  useEffect(() => {
    loadCloudData();
  }, [loadCloudData]);

  useEffect(() => {
    if (confirmDelete) {
      deleteDialogWasOpenRef.current = true;
      requestAnimationFrame(() => deleteCancelRef.current?.focus());
      return;
    }
    if (deleteDialogWasOpenRef.current) {
      deleteDialogWasOpenRef.current = false;
      requestAnimationFrame(() => cloudDeleteTriggerRef.current?.focus());
    }
  }, [confirmDelete]);

  useEffect(() => {
    if (!confirmDelete) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (deleteBusy) return;
        event.preventDefault();
        setConfirmDelete(false);
        return;
      }
      if (event.key !== 'Tab') return;

      const dialog = deleteDialogRef.current;
      if (!dialog) return;
      const focusable = focusableElements(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [confirmDelete, deleteBusy]);

  const toggleSync = useCallback(
    (next: boolean) => {
      setToggleBusy(true);
      setActionError(null);
      apiPatch<AccountResponse>('/account/preferences', {
        transcript_sync_enabled: next,
      })
        .then((res) => {
          setSyncEnabled(res.user.transcript_sync_enabled);
          void refresh();
        })
        .catch((err: unknown) => {
          setActionError(
            err instanceof ApiError ? err.message : 'Failed to update preference',
          );
        })
        .finally(() => setToggleBusy(false));
    },
    [refresh],
  );

  const doDeleteCloudHistory = useCallback(() => {
    setDeleteBusy(true);
    setActionError(null);
    apiDelete<HistoryDeleteResponse>('/history')
      .then((res) => {
        setDeletedCount(res.deleted);
        setCloudHasData(false);
        setConfirmDelete(false);
      })
      .catch((err: unknown) => {
        setActionError(
          err instanceof ApiError ? err.message : 'Failed to delete cloud history',
        );
      })
      .finally(() => setDeleteBusy(false));
  }, []);

  const canDelete = !syncEnabled && cloudHasData;

  return (
    <>
      <TopAppBar
        title="Settings"
        actions={<AccountActionButton />}
      />
      <div className="mx-auto max-w-max-width-content px-gutter pb-24 pt-24">
        <div className="relative flex flex-col gap-8 md:flex-row md:gap-10">
          {/* Left: sticky category navigation */}
          <aside className="relative hidden w-56 shrink-0 md:block">
            <div className="sticky top-8 flex flex-col gap-1">
              {CATEGORIES.map((cat) => (
                <a
                  key={cat.id}
                  href={`#${cat.id}`}
                  onClick={() => setActiveSection(cat.id)}
                  className={[
                    'flex min-h-11 min-w-11 items-center gap-3 rounded-lg px-4 py-3 font-label-lg text-label-lg transition-colors',
                    activeSection === cat.id
                      ? 'bg-primary/10 text-primary'
                      : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface',
                  ].join(' ')}
                >
                  <Icon
                    name={cat.icon}
                    fill={activeSection === cat.id}
                    className="text-[20px]"
                  />
                  {cat.label}
                </a>
              ))}
            </div>
          </aside>

          {/* Compact mobile section navigation keeps the scroll spy discoverable. */}
          <nav
            aria-label="Settings sections"
            className="no-scrollbar flex gap-1 overflow-x-auto border-b border-outline-variant/40 pb-2 md:hidden"
          >
            {CATEGORIES.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                onClick={() => setActiveSection(cat.id)}
                className={[
                  'flex min-h-11 min-w-11 shrink-0 items-center rounded-lg px-3 font-label-lg text-label-lg transition-colors focus-visible:outline-none',
                  activeSection === cat.id
                    ? 'bg-primary/10 text-primary'
                    : 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface',
                ].join(' ')}
              >
                {cat.label}
              </a>
            ))}
          </nav>

          {/* Right: grouped sections */}
          <div className="flex w-full min-w-0 max-w-3xl flex-1 flex-col gap-8">
            {actionError && (
              <div className="rounded-2xl border border-error/30 bg-error-container/40 px-4 py-3 font-body-sm text-body-sm text-on-error-container">
                {actionError}
              </div>
            )}

            {/* Account */}
            <section id="account" className="scroll-mt-24">
              <h2 className="mb-3 font-headline-md text-headline-md text-on-surface">Account</h2>
              <div className="flex flex-col overflow-hidden rounded-xl bg-surface-container-low ring-1 ring-outline-variant/30">
                {accountLoading && !user ? (
                  <div className="p-6 font-body-sm text-body-sm text-on-surface-variant">
                    Loading account…
                  </div>
                ) : accountError && !user ? (
                  <div className="flex items-center justify-between gap-4 p-6">
                    <span className="font-body-sm text-body-sm text-error">{accountError}</span>
                    <button
                      type="button"
                      onClick={() => void refresh()}
                      className="min-h-11 min-w-11 rounded-lg border border-outline-variant/50 px-3 py-1.5 font-label-lg text-label-lg text-on-surface transition-colors hover:bg-surface-container-high"
                    >
                      Retry
                    </button>
                  </div>
                ) : user ? (
                  <>
                    {/* Profile row */}
                    <div className="flex items-center justify-between gap-4 border-b border-outline-variant/20 p-6">
                      <div className="min-w-0 flex items-center gap-4">
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-outline-variant/50 bg-surface-container-high">
                          {user.picture ? (
                            <img
                              src={user.picture}
                              alt=""
                              className="h-full w-full object-cover opacity-80"
                            />
                          ) : (
                            <span className="font-title-md text-title-md text-on-surface">
                              {initialsOf(user.name)}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 flex flex-col">
                          <span className="truncate font-title-md text-title-md text-on-surface">
                            {user.name}
                          </span>
                          <span className="truncate font-body-sm text-body-sm text-on-surface-variant">
                            {user.email}
                          </span>
                        </div>
                      </div>
                      <Link
                        to="/app/account"
                        className="inline-flex min-h-11 shrink-0 items-center rounded-lg border border-outline-variant/50 bg-surface-container px-3 py-2 font-label-lg text-label-lg text-on-surface transition-colors hover:bg-surface-container-highest"
                      >
                        View account
                      </Link>
                    </div>
                    {/* Subscription row */}
                    <Link
                      to="/app/plans"
                      className="group flex cursor-pointer items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-surface-container-highest/50"
                    >
                      <div className="flex flex-1 items-center gap-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                          <Icon name="star" fill className="text-primary" />
                        </div>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-title-md text-title-md text-on-surface">
                              {user.is_pro ? 'Loaf Pro' : 'Free'}
                            </span>
                            {user.is_pro && (
                              <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 font-label-sm text-[10px] uppercase tracking-wider text-primary">
                                Active
                              </span>
                            )}
                          </div>
                          <span className="font-body-sm text-body-sm text-on-surface-variant">
                            {user.is_pro
                              ? 'Pro tier active'
                              : 'Upgrade for AI writing features'}
                          </span>
                        </div>
                      </div>
                      <Icon
                        name="chevron_right"
                        className="text-on-surface-variant transition-colors group-hover:text-on-surface"
                      />
                    </Link>
                  </>
                ) : null}
              </div>
            </section>

            {/* Appearance */}
            <section id="appearance" className="scroll-mt-24">
              <h2 className="mb-3 font-headline-md text-headline-md text-on-surface">
                Appearance
              </h2>
              <div className="flex flex-col overflow-hidden rounded-xl bg-surface-container-low ring-1 ring-outline-variant/30">
                <Row icon="dark_mode" title="Theme" subtitle="Dark" />
                <Row icon="density_medium" title="Layout density" subtitle="Comfortable" last />
              </div>
            </section>

            {/* AI Writing */}
            <section id="ai-writing" className="scroll-mt-24">
              <div className="mb-3 flex items-center gap-3">
                <Icon name="magic_button" fill className="text-primary" />
                <h2 className="font-headline-md text-headline-md text-on-surface">AI writing</h2>
              </div>
              <div className="relative flex flex-col overflow-hidden rounded-xl bg-surface-container-low ring-1 ring-outline-variant/30">
                <Row
                  icon="edit_note"
                  title="Default style output"
                  subtitle="Professional & concise"
                />
                <div className="flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-surface-container-highest/50">
                  <div className="flex flex-1 items-center gap-4">
                    <RowIcon name="bolt" />
                    <div className="flex flex-col">
                      <span className="font-title-md text-title-md text-on-surface">
                        Auto-apply suggestions
                      </span>
                      <span className="font-body-sm text-body-sm text-on-surface-variant">
                        Skip confirmation for high-confidence edits
                      </span>
                    </div>
                  </div>
                  <ObsidianToggle
                    checked={autoApply}
                    onChange={setAutoApply}
                    label="Auto-apply suggestions"
                  />
                </div>
              </div>
            </section>

            {/* Personal Dictionary */}
            <section id="dictionary" className="scroll-mt-24">
              <h2 className="mb-3 font-headline-md text-headline-md text-on-surface">
                Personal dictionary
              </h2>
              <div className="flex flex-col overflow-hidden rounded-xl bg-surface-container-low ring-1 ring-outline-variant/30">
                <Link
                  to="/app/dictionary"
                  className="group flex cursor-pointer items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-surface-container-highest/50"
                >
                  <div className="flex flex-1 items-center gap-4">
                    <RowIcon name="library_books" />
                    <div className="flex flex-col">
                      <span className="font-title-md text-title-md text-on-surface">
                        Manage vocabulary
                      </span>
                      <span className="font-body-sm text-body-sm text-on-surface-variant">
                        Custom words and phrases for dictation
                      </span>
                    </div>
                  </div>
                  <Icon
                    name="chevron_right"
                    className="text-on-surface-variant transition-colors group-hover:text-on-surface"
                  />
                </Link>
              </div>
            </section>

            {/* Privacy & Data */}
            <section id="privacy" className="scroll-mt-24">
              <h2 className="mb-3 font-headline-md text-headline-md text-on-surface">
                Privacy &amp; data
              </h2>
              <div className="flex flex-col overflow-hidden rounded-xl bg-surface-container-low ring-1 ring-outline-variant/30">
                {/* Transcript sync (real PATCH /account/preferences) */}
                <div className="flex items-center justify-between gap-4 border-b border-outline-variant/20 px-4 py-3 transition-colors hover:bg-surface-container-highest/50">
                  <div className="flex flex-1 items-center gap-4">
                    <RowIcon name="sync" />
                    <div className="flex flex-col">
                      <span className="font-title-md text-title-md text-on-surface">
                        Transcript sync
                      </span>
                      <span className="font-body-sm text-body-sm text-on-surface-variant">
                        Sync dictation and AI conversations to the cloud
                      </span>
                    </div>
                  </div>
                  <ObsidianToggle
                    checked={syncEnabled}
                    disabled={toggleBusy || accountLoading}
                    onChange={toggleSync}
                    label="Transcript sync"
                  />
                </div>
                {/* Clear cloud history (real DELETE /history) */}
                <button
                  type="button"
                  disabled={!canDelete || cloudLoading}
                  onClick={(event) => {
                    if (!canDelete) return;
                    cloudDeleteTriggerRef.current = event.currentTarget;
                    setConfirmDelete(true);
                    setDeletedCount(null);
                  }}
                  className={[
                    'group flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors',
                    canDelete
                      ? 'cursor-pointer hover:bg-error-container/30'
                      : 'cursor-default',
                  ].join(' ')}
                >
                  <div className="flex flex-1 items-center gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-container">
                      <Icon
                        name="delete"
                        className={canDelete ? 'text-error' : 'text-on-surface-variant'}
                      />
                    </div>
                    <div className="flex flex-col">
                      <span
                        className={`font-title-md text-title-md ${
                          canDelete ? 'text-error' : 'text-on-surface'
                        }`}
                      >
                        Clear Cloud History
                      </span>
                      <span className="font-body-sm text-body-sm text-on-surface-variant">
                        {cloudLoading
                          ? 'Checking cloud history…'
                          : cloudError
                            ? cloudError
                            : syncEnabled
                              ? 'Turn off transcript sync to reveal the delete option.'
                              : cloudHasData
                                ? 'Permanently delete all cloud-synced transcripts and conversations.'
                                : 'No cloud history to delete.'}
                      </span>
                    </div>
                  </div>
                  {deletedCount !== null && (
                    <span className="font-body-sm text-body-sm text-primary">
                      Deleted {deletedCount} entr{deletedCount === 1 ? 'y' : 'ies'}
                    </span>
                  )}
                </button>
              </div>
            </section>

            {/* About */}
            <section id="about" className="scroll-mt-24">
              <h2 className="mb-3 font-headline-md text-headline-md text-on-surface">About</h2>
              <div className="flex flex-col overflow-hidden rounded-xl bg-surface-container-low ring-1 ring-outline-variant/30">
                <Row icon="info" title="Loaf" subtitle="Loaf web companion" />
                <a
                  href="mailto:feedback@loaf.app"
                  className="group flex cursor-pointer items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-surface-container-highest/50"
                >
                  <div className="flex flex-1 items-center gap-4">
                    <RowIcon name="support_agent" />
                    <div className="flex flex-col">
                      <span className="font-title-md text-title-md text-on-surface">
                        Help &amp; Feedback
                      </span>
                      <span className="font-body-sm text-body-sm text-on-surface-variant">
                        Get support or send feedback
                      </span>
                    </div>
                  </div>
                  <Icon
                    name="chevron_right"
                    className="text-on-surface-variant transition-colors group-hover:text-on-surface"
                  />
                </a>
              </div>
            </section>

            {/* Legal */}
            <section id="legal" className="scroll-mt-24">
              <h2 className="mb-3 font-headline-md text-headline-md text-on-surface">Legal</h2>
              <div className="flex flex-col overflow-hidden rounded-xl bg-surface-container-low ring-1 ring-outline-variant/30">
                <Link
                  to="/privacy"
                  className="group flex cursor-pointer items-center justify-between gap-4 border-b border-outline-variant/20 px-4 py-3 transition-colors hover:bg-surface-container-highest/50"
                >
                  <div className="flex flex-1 items-center gap-4">
                    <RowIcon name="policy" />
                    <span className="font-title-md text-title-md text-on-surface">
                      Privacy Policy
                    </span>
                  </div>
                  <Icon
                    name="chevron_right"
                    className="text-on-surface-variant transition-colors group-hover:text-on-surface"
                  />
                </Link>
                <Link
                  to="/terms"
                  className="group flex cursor-pointer items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-surface-container-highest/50"
                >
                  <div className="flex flex-1 items-center gap-4">
                    <RowIcon name="description" />
                    <span className="font-title-md text-title-md text-on-surface">
                      Terms of Service
                    </span>
                  </div>
                  <Icon
                    name="chevron_right"
                    className="text-on-surface-variant transition-colors group-hover:text-on-surface"
                  />
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !deleteBusy && setConfirmDelete(false)}
        >
          <div
            ref={deleteDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cloud-history-delete-dialog-title"
            aria-describedby="cloud-history-delete-dialog-description"
            tabIndex={-1}
            className="w-full max-w-sm rounded-2xl border border-outline-variant/30 bg-surface-container p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="cloud-history-delete-dialog-title"
              className="font-headline-md text-headline-md text-on-surface"
            >
              Delete all cloud history?
            </h3>
            <p
              id="cloud-history-delete-dialog-description"
              className="mt-2 font-body-md text-body-md text-on-surface-variant"
            >
              This permanently removes every cloud-synced transcript and conversation. This action
              cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                ref={deleteCancelRef}
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleteBusy}
                className="min-h-11 rounded-lg border border-outline-variant/50 px-4 py-2 font-label-lg text-label-lg text-on-surface transition-colors hover:bg-surface-container-high disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doDeleteCloudHistory}
                disabled={deleteBusy}
                className="min-h-11 rounded-lg bg-error px-4 py-2 font-label-lg text-label-lg text-on-error transition-colors hover:opacity-90 disabled:opacity-50"
              >
                {deleteBusy ? 'Deleting…' : 'Delete everything'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Circular icon container used at the start of every settings row. */
function RowIcon({ name, fill, className }: { name: string; fill?: boolean; className?: string }) {
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-container">
      <Icon name={name} fill={fill} className={className ?? 'text-on-surface-variant'} />
    </div>
  );
}

interface RowProps {
  icon: string;
  title: string;
  subtitle?: string;
  chevron?: boolean;
  trailingIcon?: string;
  last?: boolean;
  to?: string;
  onClick?: () => void;
}

/** A single settings row: leading icon circle, title/subtitle, trailing chevron or icon. */
function Row({ icon, title, subtitle, chevron, trailingIcon, last, to, onClick }: RowProps) {
  const cls = [
    'group flex items-center justify-between gap-4 px-4 py-3',
    last ? '' : 'border-b border-outline-variant/20',
    to || onClick
      ? 'cursor-pointer transition-colors hover:bg-surface-container-highest/50'
      : '',
  ].join(' ');

  const inner = (
    <>
      <div className="flex flex-1 items-center gap-4">
        <RowIcon name={icon} />
        <div className="flex flex-col">
          <span className="font-title-md text-title-md text-on-surface">{title}</span>
          {subtitle && (
            <span className="font-body-sm text-body-sm text-on-surface-variant">{subtitle}</span>
          )}
        </div>
      </div>
      {chevron && (
        <Icon
          name="chevron_right"
          className="text-on-surface-variant transition-colors group-hover:text-on-surface"
        />
      )}
      {trailingIcon && (
        <Icon
          name={trailingIcon}
          className="text-on-surface-variant transition-colors group-hover:text-on-surface"
        />
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cls}>
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${cls} text-left`}>
        {inner}
      </button>
    );
  }
  return <div className={cls}>{inner}</div>;
}

/** Obsidian toggle (peer-checked:bg-primary) matching the Stitch mockup. */
function ObsidianToggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <label
      className="relative inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center"
      aria-label={label}
    >
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div className="relative h-6 w-11 rounded-full border border-outline-variant/30 bg-surface-container-highest peer-checked:bg-primary peer-disabled:cursor-not-allowed peer-disabled:opacity-50 peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-container-low after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-on-surface after:transition-all peer-checked:after:translate-x-[20px] after:content-['']" />
    </label>
  );
}
